"use client";

import { type ReactNode, useSyncExternalStore } from "react";
import { PrivyProvider } from "@privy-io/react-auth";
import { PrivyNextAuthBridge } from "@/components/PrivyNextAuthBridge";

const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID || "";

// Privy's SDK refuses to initialize outside a secure context (HTTPS or
// localhost), so only mount the provider when the origin qualifies.
// window.location doesn't change during a session — useSyncExternalStore is
// the idiomatic React 18+ way to read that kind of external signal without
// tripping the "setState in effect" lint rule.
const subscribeNoop = () => () => {};

function getClientSnapshot(): boolean {
  const isHttps = window.location.protocol === "https:";
  const isLocalhost = window.location.hostname === "localhost";
  return !!appId && (isHttps || isLocalhost);
}

function getServerSnapshot(): boolean {
  return false;
}

interface WalletConnectProviderProps {
  children: ReactNode;
}

/**
 * Returns true when PrivyProvider is mounted (i.e. the origin qualifies and
 * NEXT_PUBLIC_PRIVY_APP_ID is set). Consumers that call usePrivy() must gate
 * on this first — usePrivy() throws when the provider isn't in the tree.
 */
export function usePrivyAvailable(): boolean {
  return useSyncExternalStore(
    subscribeNoop,
    getClientSnapshot,
    getServerSnapshot,
  );
}

export function WalletConnectProvider({ children }: WalletConnectProviderProps) {
  const canUsePrivy = usePrivyAvailable();

  if (!canUsePrivy) {
    return <>{children}</>;
  }

  return (
    <PrivyProvider
      appId={appId}
      config={{
        appearance: {
          theme: "light",
          accentColor: "#3B82F6",
          // Email OTP and MetaMask are the only sanctioned ways in. Coinbase
          // Wallet and Phantom (Solana) are intentionally omitted for now.
          // Order here is the modal order.
          walletList: ["metamask"],
        },
        // The only two account methods across the whole app: email OTP, or
        // connecting an external wallet. No socials, no embedded wallets.
        loginMethods: ["email", "wallet"],
        embeddedWallets: {
          ethereum: {
            createOnLogin: "off",
          },
        },
      }}
    >
      <PrivyNextAuthBridge />
      {children}
    </PrivyProvider>
  );
}
